import { FunctionComponent as F, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeCountryAsync } from "../../core/store/user/user.thunks";
import { IState } from "../../core/store/store";
import { useAppDispatch } from "../../core/store/hooks";
import styled from "@emotion/styled";
import { Box, Autocomplete, TextField } from "@mui/material";
import { getCountriesList } from "../../core/http/user";
import { ICountry, IOption } from "@protoplan/types";
import { updateProtocols } from "../../core/store/protocol/protocol.slice";


interface TCountryOption extends IOption
{
  popularity: number;
}

const CountrySelect: F = () =>
{
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const countryId = useSelector((state: IState) => state.user.countryId);
  const [countries, setCountries] = useState<ICountry[]>([]);

  const selectedCountry = countries.find(c => c.countryId === countryId);

  const generateOption = (country: ICountry) => ({
    value: country.countryId,
    popularity: country.popularity,
    // Addition of country.pop controlling trailing space prevents duplicated keys
    label: country.name + " (" + country.currency.code + ")" + (country.popularity !== 0 ? '' : ' ' )
  });

  const determineOptions = (countries: ICountry[]) => countries.map(generateOption);

  const [countryOptions, setOptions] = useState(countries.map(generateOption));

  const [currentCountryOption, setCurrentCountryOption] = useState(selectedCountry ? {
    value: selectedCountry.countryId,
    label: selectedCountry.name,
    popularity: selectedCountry.popularity } : undefined);

  useEffect(() => setOptions(countries.map(generateOption)), [countries]);

  useEffect(() =>
  {
    setCurrentCountryOption(selectedCountry ? {
      value: selectedCountry.countryId,
      popularity: selectedCountry.popularity,
      label: selectedCountry.name } : undefined);
  }, [selectedCountry]);

  useEffect(() =>
  {
    getCountriesList()
      .then(data =>
      {
        if(!data)
          throw(Error("Failed to retrieve countries"));

        setCountries(data);
        setOptions(determineOptions(data));
      })
      .catch(err => console.log(err));
  }, [countryId]);


  async function handleCountryChange(
    _event: React.SyntheticEvent<Element, Event>,
    value: { value: number, label: string, popularity: number })
  {
    if(!value)
      return;

    setCurrentCountryOption(value);
    const newCountry = countries.filter(c => c.countryId === value.value)[0];

    await appDispatch(changeCountryAsync({
      countryId: newCountry.countryId,
      currencyCode: newCountry.currency.code,
      currencySymbol: newCountry.currency.symbol || newCountry.currency.code }));

    dispatch(updateProtocols([]));
  }

  const Option = styled('li')({ fontSize: 15, mt: -0.8, mb: -0.8 });

  return(
    <Box>
      {currentCountryOption ?
        <Autocomplete
          id='Country'
          size='small'
          sx={{
            width: 300,
            '& .MuiAutocomplete-input, & .MuiInputLabel-root': { fontSize: 15, mt: -0.6, mb: -0.6 }}}
          renderOption={(props: any, option: TCountryOption) =>
            <Option {...props}
              style={{ backgroundColor: option.popularity? option.popularity > 0 ? '#C8FFC8' : '' : '' }} >
              {option.label}
            </Option>}
          blurOnSelect
          value={currentCountryOption}
          // Use option value to determine the selected option (label used by default)
          // Important as labels need to differ for repeated countries to prevent duplicate keys
          isOptionEqualToValue={(option, value) => option.value === value.value}
          options={countryOptions}
          onChange={handleCountryChange}
          disableClearable
          renderInput={(params: any) => <TextField {...params}/>} /> :
        <></>}
    </Box>);
};

export default CountrySelect;
